/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_VESTING_PROGRAM_ADDRESS } from '../programs';

export const CLAIM_DISCRIMINATOR = new Uint8Array([
  62, 198, 214, 193, 213, 159, 108, 210,
]);

export function getClaimDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(CLAIM_DISCRIMINATOR);
}

export type ClaimInstruction<
  TProgram extends string = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountBeneficiary extends string | AccountMeta<string> = string,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountVestingConfig extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountVestingVault extends string | AccountMeta<string> = string,
  TAccountBeneficiaryTokenAccount extends string | AccountMeta<string> = string,
  TAccountBaseTokenProgram extends string | AccountMeta<string> = string,
  TAccountAssociatedTokenProgram extends string | AccountMeta<string> =
    'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL',
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountBeneficiary extends string
        ? ReadonlyAccount<TAccountBeneficiary>
        : TAccountBeneficiary,
      TAccountLaunch extends string
        ? ReadonlyAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountVestingConfig extends string
        ? WritableAccount<TAccountVestingConfig>
        : TAccountVestingConfig,
      TAccountBaseMint extends string
        ? ReadonlyAccount<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountVestingVault extends string
        ? WritableAccount<TAccountVestingVault>
        : TAccountVestingVault,
      TAccountBeneficiaryTokenAccount extends string
        ? WritableAccount<TAccountBeneficiaryTokenAccount>
        : TAccountBeneficiaryTokenAccount,
      TAccountBaseTokenProgram extends string
        ? ReadonlyAccount<TAccountBaseTokenProgram>
        : TAccountBaseTokenProgram,
      TAccountAssociatedTokenProgram extends string
        ? ReadonlyAccount<TAccountAssociatedTokenProgram>
        : TAccountAssociatedTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type ClaimInstructionData = {
  discriminator: ReadonlyUint8Array;
  scheduleId: Option<number>;
  amount: bigint;
};

export type ClaimInstructionDataArgs = {
  scheduleId: OptionOrNullable<number>;
  amount: number | bigint;
};

export function getClaimInstructionDataEncoder(): Encoder<ClaimInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['scheduleId', getOptionEncoder(getU8Encoder())],
      ['amount', getU64Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CLAIM_DISCRIMINATOR }),
  );
}

export function getClaimInstructionDataDecoder(): Decoder<ClaimInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['scheduleId', getOptionDecoder(getU8Decoder())],
    ['amount', getU64Decoder()],
  ]);
}

export function getClaimInstructionDataCodec(): Codec<
  ClaimInstructionDataArgs,
  ClaimInstructionData
> {
  return combineCodec(
    getClaimInstructionDataEncoder(),
    getClaimInstructionDataDecoder(),
  );
}

export type ClaimAsyncInput<
  TAccountPayer extends string = string,
  TAccountBeneficiary extends string = string,
  TAccountLaunch extends string = string,
  TAccountVestingConfig extends string = string,
  TAccountBaseMint extends string = string,
  TAccountVestingVault extends string = string,
  TAccountBeneficiaryTokenAccount extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountAssociatedTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  beneficiary: Address<TAccountBeneficiary>;
  launch: Address<TAccountLaunch>;
  vestingConfig?: Address<TAccountVestingConfig>;
  baseMint: Address<TAccountBaseMint>;
  vestingVault?: Address<TAccountVestingVault>;
  beneficiaryTokenAccount?: Address<TAccountBeneficiaryTokenAccount>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  associatedTokenProgram?: Address<TAccountAssociatedTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  scheduleId: ClaimInstructionDataArgs['scheduleId'];
  amount: ClaimInstructionDataArgs['amount'];
};

export async function getClaimInstructionAsync<
  TAccountPayer extends string,
  TAccountBeneficiary extends string,
  TAccountLaunch extends string,
  TAccountVestingConfig extends string,
  TAccountBaseMint extends string,
  TAccountVestingVault extends string,
  TAccountBeneficiaryTokenAccount extends string,
  TAccountBaseTokenProgram extends string,
  TAccountAssociatedTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
>(
  input: ClaimAsyncInput<
    TAccountPayer,
    TAccountBeneficiary,
    TAccountLaunch,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountVestingVault,
    TAccountBeneficiaryTokenAccount,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ClaimInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountBeneficiary,
    TAccountLaunch,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountVestingVault,
    TAccountBeneficiaryTokenAccount,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_VESTING_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    beneficiary: { value: input.beneficiary ?? null, isWritable: false },
    launch: { value: input.launch ?? null, isWritable: false },
    vestingConfig: { value: input.vestingConfig ?? null, isWritable: true },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    vestingVault: { value: input.vestingVault ?? null, isWritable: true },
    beneficiaryTokenAccount: {
      value: input.beneficiaryTokenAccount ?? null,
      isWritable: true,
    },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    associatedTokenProgram: {
      value: input.associatedTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.vestingConfig.value) {
    accounts.vestingConfig.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            118, 101, 115, 116, 105, 110, 103, 95, 99, 111, 110, 102, 105, 103,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.vestingVault.value) {
    accounts.vestingVault.value = await getProgramDerivedAddress({
      programAddress:
        'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>,
      seeds: [
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'vestingConfig',
            accounts.vestingConfig.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseTokenProgram',
            accounts.baseTokenProgram.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.beneficiaryTokenAccount.value) {
    accounts.beneficiaryTokenAccount.value = await getProgramDerivedAddress({
      programAddress:
        'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>,
      seeds: [
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'beneficiary',
            accounts.beneficiary.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseTokenProgram',
            accounts.baseTokenProgram.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.associatedTokenProgram.value) {
    accounts.associatedTokenProgram.value =
      'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('beneficiary', accounts.beneficiary),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('vestingConfig', accounts.vestingConfig),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('vestingVault', accounts.vestingVault),
      getAccountMeta(
        'beneficiaryTokenAccount',
        accounts.beneficiaryTokenAccount,
      ),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('associatedTokenProgram', accounts.associatedTokenProgram),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getClaimInstructionDataEncoder().encode(
      args as ClaimInstructionDataArgs,
    ),
    programAddress,
  } as ClaimInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountBeneficiary,
    TAccountLaunch,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountVestingVault,
    TAccountBeneficiaryTokenAccount,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >);
}

export type ClaimInput<
  TAccountPayer extends string = string,
  TAccountBeneficiary extends string = string,
  TAccountLaunch extends string = string,
  TAccountVestingConfig extends string = string,
  TAccountBaseMint extends string = string,
  TAccountVestingVault extends string = string,
  TAccountBeneficiaryTokenAccount extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountAssociatedTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  payer: TransactionSigner<TAccountPayer>;
  beneficiary: Address<TAccountBeneficiary>;
  launch: Address<TAccountLaunch>;
  vestingConfig: Address<TAccountVestingConfig>;
  baseMint: Address<TAccountBaseMint>;
  vestingVault: Address<TAccountVestingVault>;
  beneficiaryTokenAccount: Address<TAccountBeneficiaryTokenAccount>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  associatedTokenProgram?: Address<TAccountAssociatedTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  scheduleId: ClaimInstructionDataArgs['scheduleId'];
  amount: ClaimInstructionDataArgs['amount'];
};

export function getClaimInstruction<
  TAccountPayer extends string,
  TAccountBeneficiary extends string,
  TAccountLaunch extends string,
  TAccountVestingConfig extends string,
  TAccountBaseMint extends string,
  TAccountVestingVault extends string,
  TAccountBeneficiaryTokenAccount extends string,
  TAccountBaseTokenProgram extends string,
  TAccountAssociatedTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
>(
  input: ClaimInput<
    TAccountPayer,
    TAccountBeneficiary,
    TAccountLaunch,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountVestingVault,
    TAccountBeneficiaryTokenAccount,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): ClaimInstruction<
  TProgramAddress,
  TAccountPayer,
  TAccountBeneficiary,
  TAccountLaunch,
  TAccountVestingConfig,
  TAccountBaseMint,
  TAccountVestingVault,
  TAccountBeneficiaryTokenAccount,
  TAccountBaseTokenProgram,
  TAccountAssociatedTokenProgram,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_VESTING_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    payer: { value: input.payer ?? null, isWritable: true },
    beneficiary: { value: input.beneficiary ?? null, isWritable: false },
    launch: { value: input.launch ?? null, isWritable: false },
    vestingConfig: { value: input.vestingConfig ?? null, isWritable: true },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    vestingVault: { value: input.vestingVault ?? null, isWritable: true },
    beneficiaryTokenAccount: {
      value: input.beneficiaryTokenAccount ?? null,
      isWritable: true,
    },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    associatedTokenProgram: {
      value: input.associatedTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.associatedTokenProgram.value) {
    accounts.associatedTokenProgram.value =
      'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('beneficiary', accounts.beneficiary),
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('vestingConfig', accounts.vestingConfig),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('vestingVault', accounts.vestingVault),
      getAccountMeta(
        'beneficiaryTokenAccount',
        accounts.beneficiaryTokenAccount,
      ),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('associatedTokenProgram', accounts.associatedTokenProgram),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getClaimInstructionDataEncoder().encode(
      args as ClaimInstructionDataArgs,
    ),
    programAddress,
  } as ClaimInstruction<
    TProgramAddress,
    TAccountPayer,
    TAccountBeneficiary,
    TAccountLaunch,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountVestingVault,
    TAccountBeneficiaryTokenAccount,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >);
}

export type ParsedClaimInstruction<
  TProgram extends string = typeof DOPPLER_VESTING_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    payer: TAccountMetas[0];
    beneficiary: TAccountMetas[1];
    launch: TAccountMetas[2];
    vestingConfig: TAccountMetas[3];
    baseMint: TAccountMetas[4];
    vestingVault: TAccountMetas[5];
    beneficiaryTokenAccount: TAccountMetas[6];
    baseTokenProgram: TAccountMetas[7];
    associatedTokenProgram: TAccountMetas[8];
    systemProgram: TAccountMetas[9];
  };
  data: ClaimInstructionData;
};

export function parseClaimInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedClaimInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 10) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 10,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      payer: getNextAccount(),
      beneficiary: getNextAccount(),
      launch: getNextAccount(),
      vestingConfig: getNextAccount(),
      baseMint: getNextAccount(),
      vestingVault: getNextAccount(),
      beneficiaryTokenAccount: getNextAccount(),
      baseTokenProgram: getNextAccount(),
      associatedTokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getClaimInstructionDataDecoder().decode(instruction.data),
  };
}
